<script setup>
import { defineProps, defineEmits, onMounted, onBeforeUnmount, ref, watch, computed, nextTick } from 'vue';
import { getReferenceFile } from '@/services/taskReviewService';
import MarkdownRenderer from '@/components/MarkdownRenderer.vue';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    task: {
        type: Object,
        required: true
    },
    evalId: {
        type: String,
        required: true
    },
    totalTasks: {
        type: Number,
        required: true
    },
    currentTaskIndex: {
        type: Number,
        required: true
    }
});

const emit = defineEmits(['close', 'prev', 'next']);

const referenceFiles = computed(() => {
    const rf = props.task.reference_file;
    if (!rf) return [];
    if (Array.isArray(rf)) return rf.filter(f => typeof f === 'string' && f.trim().length > 0);
    if (typeof rf === 'string' && rf.trim().length > 0) return [rf];
    return [];
});

const hasReferenceFiles = computed(() => referenceFiles.value.length > 0);
const hasMultipleFiles = computed(() => referenceFiles.value.length > 1);

const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop().toLowerCase();
    return IMAGE_EXTENSIONS.includes(ext);
};

const orderedFiles = computed(() => {
    const all = referenceFiles.value.map((f, i) => ({ name: f, originalIndex: i }));
    const images = all.filter(f => isImageFile(f.name));
    const others = all.filter(f => !isImageFile(f.name));
    return [...images, ...others];
});

const imageFiles = computed(() => orderedFiles.value.filter(f => isImageFile(f.name)));
const otherFiles = computed(() => orderedFiles.value.filter(f => !isImageFile(f.name)));

const selectedOrderedIndex = ref(0);
const selectedFile = computed(() => orderedFiles.value[selectedOrderedIndex.value]?.name || null);

const referenceFileUrl = ref(null);
const referenceFileZoom = ref(1);
const fileIsImage = ref(true);
const loadingReferenceFile = ref(false);

const thumbnailUrls = ref({});
const loadingThumbnails = ref(new Set());
const referenceFileUrls = ref({});

const fetchReferenceFile = async () => {
    if (!selectedFile.value) return;
    loadingReferenceFile.value = true;
    fileIsImage.value = isImageFile(selectedFile.value);

    if (!fileIsImage.value) {
        loadingReferenceFile.value = false;
        return;
    }

    const cachedUrl = referenceFileUrls.value[selectedOrderedIndex.value];
    if (cachedUrl) {
        referenceFileUrl.value = cachedUrl;
        loadingReferenceFile.value = false;
        return;
    }

    const file = await getReferenceFile(props.evalId, props.task.task_id, true, selectedFile.value);
    if (file) {
        const objectUrl = URL.createObjectURL(file);
        referenceFileUrls.value = {
            ...referenceFileUrls.value,
            [selectedOrderedIndex.value]: objectUrl
        };
        referenceFileUrl.value = objectUrl;
    }
    loadingReferenceFile.value = false;
};

const fetchThumbnails = async () => {
    for (const filename of referenceFiles.value) {
        if (!isImageFile(filename) || thumbnailUrls.value[filename]) continue;
        loadingThumbnails.value.add(filename);

        const file = await getReferenceFile(props.evalId, props.task.task_id, true, filename);
        if (file) {
            thumbnailUrls.value[filename] = URL.createObjectURL(file);
        }

        loadingThumbnails.value.delete(filename);
    }
};

const thumbRefs = ref({});
const setThumbRef = (index, el) => {
    if (el) thumbRefs.value[index] = el;
};

const preloadAdjacentFile = (orderedIdx) => {
    const adjacentIndices = [orderedIdx - 1, orderedIdx + 1].filter(
        i => i >= 0 && i < orderedFiles.value.length
    );
    for (const i of adjacentIndices) {
        const entry = orderedFiles.value[i];
        if (!entry || !isImageFile(entry.name) || referenceFileUrls.value[i]) continue;
        getReferenceFile(props.evalId, props.task.task_id, true, entry.name).then(file => {
            if (file && !referenceFileUrls.value[i]) {
                referenceFileUrls.value = {
                    ...referenceFileUrls.value,
                    [i]: URL.createObjectURL(file)
                };
            }
        });
    }
};

const selectFile = (orderedIdx) => {
    if (orderedIdx < 0 || orderedIdx >= orderedFiles.value.length) return;
    selectedOrderedIndex.value = orderedIdx;
    resetZoom();
    fetchReferenceFile();
    preloadAdjacentFile(orderedIdx);
    nextTick(() => {
        thumbRefs.value[orderedIdx]?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
        });
    });
};

const selectPrevFile = () => {
    if (selectedOrderedIndex.value > 0) {
        selectFile(selectedOrderedIndex.value - 1);
    }
};

const selectNextFile = () => {
    if (selectedOrderedIndex.value < orderedFiles.value.length - 1) {
        selectFile(selectedOrderedIndex.value + 1);
    }
};

const isFirstFile = computed(() => selectedOrderedIndex.value === 0);
const isLastFile = computed(() => selectedOrderedIndex.value === orderedFiles.value.length - 1);

const zoomIn = () => {
    if (referenceFileZoom.value >= 5) return;
    referenceFileZoom.value = Math.min(referenceFileZoom.value + 0.1, 5);
};

const zoomOut = () => {
    if (referenceFileZoom.value <= 1) {
        referenceFileZoom.value = 1;
        panX.value = 0;
        panY.value = 0;
        return;
    }
    referenceFileZoom.value = Math.max(referenceFileZoom.value - 0.1, 1);
};

const resetZoom = () => {
    referenceFileZoom.value = 1;
    panX.value = 0;
    panY.value = 0;
};

const zoomToFit = resetZoom;

const panX = ref(0);
const panY = ref(0);
const isDragging = ref(false);
const startX = ref(0);
const startY = ref(0);
const containerRef = ref(null);

const startDrag = (event) => {
    if(referenceFileZoom.value <= 1) return;
    event.preventDefault();
    isDragging.value = true;
    startX.value = event.clientX - panX.value;
    startY.value = event.clientY - panY.value;
    globalThis.addEventListener('mousemove', onDrag);
    globalThis.addEventListener('mouseup', stopDrag);
};

const onDrag = (event) => {
    if (!isDragging.value || !containerRef.value) return;

    const newPanX = event.clientX - startX.value;
    const newPanY = event.clientY - startY.value;

    const containerWidth = containerRef.value.offsetWidth;
    const containerHeight = containerRef.value.offsetHeight;
    
    const maxPanX = (containerWidth * referenceFileZoom.value - containerWidth) / 2;
    const maxPanY = (containerHeight * referenceFileZoom.value - containerHeight) / 2;

    panX.value = Math.min(Math.max(newPanX, -maxPanX), maxPanX);
    panY.value = Math.min(Math.max(newPanY, -maxPanY), maxPanY);
};

const stopDrag = () => {
    isDragging.value = false;
    globalThis.removeEventListener('mousemove', onDrag);
    globalThis.removeEventListener('mouseup', stopDrag);
};

const zoomPercentage = computed(() => {
    return ((referenceFileZoom.value - 1) / 4) * 100;
});

const handlePrevTask = () => {
    emit('prev');
};

const handleNextTask = () => {
    emit('next');
};

const promptCopied = ref(false);
const copyPrompt = () => {
    navigator.clipboard.writeText(props.task.task_prompt);
    promptCopied.value = true;
    setTimeout(() => {
        promptCopied.value = false;
    }, 1000);
};

const showZoomIndicator = ref(false);
const zoomIndicatorTimer = ref(null);

watch(referenceFileZoom, (newZoom) => {
    if (newZoom <= 1) {
        resetZoom();
        showZoomIndicator.value = false;
    } else if (containerRef.value) {
        showZoomIndicator.value = true;
        if(zoomIndicatorTimer.value) clearTimeout(zoomIndicatorTimer.value);
        zoomIndicatorTimer.value = setTimeout(() => {
            showZoomIndicator.value = false;
        }, 2000);

        const containerWidth = containerRef.value.offsetWidth;
        const containerHeight = containerRef.value.offsetHeight;
        const maxPanX = (containerWidth * newZoom - containerWidth) / 2;
        const maxPanY = (containerHeight * newZoom - containerHeight) / 2;
        
        panX.value = Math.min(Math.max(panX.value, -maxPanX), maxPanX);
        panY.value = Math.min(Math.max(panY.value, -maxPanY), maxPanY);
    }
});

const revokeAllUrls = () => {
    if (referenceFileUrl.value) {
        URL.revokeObjectURL(referenceFileUrl.value);
        referenceFileUrl.value = null;
    }
    for (const url of Object.values(thumbnailUrls.value)) {
        URL.revokeObjectURL(url);
    }
    thumbnailUrls.value = {};
    for (const url of Object.values(referenceFileUrls.value)) {
        URL.revokeObjectURL(url);
    }
    referenceFileUrls.value = {};
};

watch(() => props.task, async () => {
    revokeAllUrls();
    selectedOrderedIndex.value = 0;
    resetZoom();
    await fetchReferenceFile();
    if (hasMultipleFiles.value) {
        fetchThumbnails();
    }
});

const handleKeydown = (e) => {
    if (!props.show) return;

    if (e.key === 'Escape') {
        emit('close');
    } else if (e.key === 'ArrowLeft') {
        handlePrevTask();
    } else if (e.key === 'ArrowRight') {
        handleNextTask();
    } else if (e.key === 'ArrowUp' && hasMultipleFiles.value) {
        e.preventDefault();
        selectPrevFile();
    } else if (e.key === 'ArrowDown' && hasMultipleFiles.value) {
        e.preventDefault();
        selectNextFile();
    }
};

const handleWheel = (event) => {
    if (!fileIsImage.value) return;
    event.preventDefault();
    if (event.deltaY < 0) {
        zoomIn();
    } else {
        zoomOut();
    }
};

const formatFilename = (filename, maxLength = 30) => {
    if(filename && filename.length > maxLength){
        const half = Math.floor(maxLength / 2);
        return filename.substring(0, half) + '...' + filename.substring(filename.length - half);
    }
    return filename;
};

const getFileIcon = (filename) => {
    if (isImageFile(filename)) return 'mdi-file-image-outline';
    const ext = filename?.split('.').pop()?.toLowerCase();
    const iconMap = {
        pdf: 'mdi-file-pdf-box',
        doc: 'mdi-file-word-outline',
        docx: 'mdi-file-word-outline',
        txt: 'mdi-file-document-outline',
        csv: 'mdi-file-table-outline',
        json: 'mdi-code-json',
        zip: 'mdi-folder-zip-outline',
        mp4: 'mdi-file-video-outline',
        mp3: 'mdi-file-music-outline',
    };
    return iconMap[ext] || 'mdi-file-outline';
};

onMounted(async () => {
    await fetchReferenceFile();
    if (hasMultipleFiles.value) {
        fetchThumbnails();
    }
    globalThis.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
    globalThis.removeEventListener('keydown', handleKeydown);
    revokeAllUrls();
    if(zoomIndicatorTimer.value) clearTimeout(zoomIndicatorTimer.value);
});
</script>
<template>
    <Teleport to="body">
        <div
            v-if="props.show"
            class="fixed inset-0 z-[9999] flex items-center justify-center bg-echoes-background-black/50 backdrop-blur-sm"
            data-modal
            @click.self="emit('close')"
        >
            <div ref="modalRef" class="bg-echoes-background-gray rounded-[21px] shadow-xl w-[60dvw] max-h-[85dvh] animate-slideIn border border-echoes-border-light flex flex-col">
                <!-- Header (fixed) -->
                <div class="flex items-center justify-between px-5 py-3 border-b border-echoes-border-light shrink-0">
                    <div class="flex items-center gap-2">
                        <i class="mdi mdi-file-document-outline text-echoes-primary text-lg"></i>
                        <h2 class="font-semibold">Task Viewer</h2>
                    </div>
                    <div class="flex items-center gap-2 text-sm">
                        <button class="pe-1.5 ps-1 border border-echoes-primary rounded-lg hover:bg-echoes-primary transition-colors duration-300 ease-in-out" @click="handlePrevTask">
                            <i class="mdi mdi-chevron-left text-lg"></i>
                        </button>
                        <div class="text-white/80 text-sm tabular-nums">
                            {{ props.currentTaskIndex + 1 }} of {{ props.totalTasks }}
                        </div>
                        <button class="px-1 border border-echoes-primary rounded-lg hover:bg-echoes-primary transition-colors duration-300 ease-in-out" @click="handleNextTask">
                            <i class="mdi mdi-chevron-right text-lg"></i>
                        </button>
                    </div>
                    <button class="p-1 text-white/60 hover:text-white hover:bg-white/10 transition-colors duration-200 rounded-lg" @click="emit('close')">
                        <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <!-- Body (scrollable) -->
                <div class="flex-1 overflow-y-auto custom-scrollbar p-4">
                    <!-- Task ID -->
                    <div class="flex items-center gap-2 mb-3">
                        <span class="text-xs text-white/50 uppercase tracking-wider">Task ID</span>
                        <span class="text-sm font-mono text-white/80 bg-white/5 px-2 py-0.5 rounded">{{ props.task.task_id }}</span>
                    </div>

                    <div class="grid gap-4" :class="hasReferenceFiles ? 'grid-cols-5' : 'grid-cols-1'">
                        <!-- Task Prompt -->
                        <div class="rounded-lg bg-echoes-background-black p-4 border border-echoes-border-light flex flex-col gap-2" :class="hasReferenceFiles ? 'col-span-3' : 'col-span-1'">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center gap-2">
                                    <i class="mdi mdi-text text-white/50"></i>
                                    <span class="font-semibold text-sm">Task Prompt</span>
                                </div>
                                <div class="button-copy" @click="copyPrompt">
                                    <i class="mdi" :class="promptCopied ? 'mdi-check text-green-400' : 'mdi-content-copy'"></i>
                                    {{ promptCopied ? 'Copied!' : 'Copy' }}
                                </div>
                            </div>
                            <div class="text-white/80 text-sm task-content">
                                <MarkdownRenderer :model-value="props.task.task_prompt" :content-only="true" />
                            </div>
                        </div>

                        <!-- Single reference file -->
                        <div
                            v-if="hasReferenceFiles && !hasMultipleFiles"
                            class="col-span-2 rounded-lg bg-echoes-background-black p-4 border border-echoes-border-light flex flex-col gap-2"
                        >
                            <div class="flex items-center gap-2">
                                <i class="mdi mdi-file-image-outline text-white/50"></i>
                                <span class="font-semibold text-sm">Reference File</span>
                            </div>
                            <div class="text-white/60 text-xs truncate" :title="referenceFiles[0]">
                                {{ formatFilename(referenceFiles[0]) }}
                            </div>
                            <div class="flex flex-col gap-2 flex-1 min-h-0">
                                <div 
                                    ref="containerRef"
                                    class="overflow-hidden w-full flex-1 min-h-[200px] max-h-[50dvh] rounded-md cursor-grab relative bg-echoes-background-gray"
                                    :class="{ 'cursor-grabbing': isDragging }"
                                    @mousedown="startDrag"
                                    @wheel="handleWheel"
                                    v-if="fileIsImage"
                                >
                                    <img 
                                        :src="referenceFileUrl" 
                                        alt="Reference File" 
                                        v-if="!loadingReferenceFile"
                                        class="w-full h-full object-cover" 
                                        :class="{ 'transition-transform duration-200': !isDragging }"
                                        :style="{ transform: `translate(${panX}px, ${panY}px) scale(${referenceFileZoom})` }"
                                    >
                                    <div v-else class="w-full h-full flex items-center justify-center min-h-[200px]">
                                        <div class="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-echoes-primary"></div>
                                    </div>

                                    <div 
                                        v-if="showZoomIndicator"
                                        class="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1 pointer-events-none transition-opacity duration-300"
                                    >
                                        <i class="mdi mdi-magnify"></i>
                                        {{ referenceFileZoom.toFixed(1) }}x
                                    </div>
                                </div>
                                <div v-else class="w-full flex-1 min-h-[120px] flex flex-col items-center justify-center gap-2 bg-echoes-background-gray rounded-md border border-echoes-border-light text-white/50">
                                    <i class="mdi mdi-file-eye-off-outline text-4xl"></i>
                                    <span class="text-sm">Preview not available for this file type</span>
                                </div>
                                <div class="flex items-center justify-between gap-2 select-none" v-if="fileIsImage">
                                    <div :class="referenceFileZoom <= 1 ? 'button-zoom-disabled' : 'button-zoom'" @click="zoomToFit">
                                        <i class="mdi mdi-arrow-expand-all"></i>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <div :class="referenceFileZoom <= 1 ? 'button-zoom-disabled' : 'button-zoom'" @click="zoomOut">
                                            <i class="mdi mdi-minus"></i>
                                        </div>
                                        <input 
                                            type="range" 
                                            min="1" 
                                            max="5" 
                                            step="0.1" 
                                            v-model.number="referenceFileZoom" 
                                            class="w-full h-2 bg-neutral-quaternary rounded-full appearance-none cursor-pointer"
                                            :style="{ backgroundImage: `linear-gradient(to right, #FE3E01 0%, #FE3E01 ${zoomPercentage}%, transparent ${zoomPercentage}%, transparent 100%)` }"
                                        >
                                        <div :class="referenceFileZoom >= 5 ? 'button-zoom-disabled' : 'button-zoom'" @click="zoomIn">
                                            <i class="mdi mdi-plus"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Multiple reference files -->
                        <div
                            v-if="hasMultipleFiles"
                            class="col-span-2 rounded-lg bg-echoes-background-black p-3 border border-echoes-border-light flex flex-col gap-2"
                        >
                            <!-- Header -->
                            <div class="flex items-center justify-between shrink-0">
                                <div class="flex items-center gap-2">
                                    <i class="mdi mdi-file-multiple-outline text-white/50"></i>
                                    <span class="font-semibold text-sm">Reference Files</span>
                                </div>
                                <span class="text-[11px] text-white/50 bg-white/10 rounded-full px-2 py-0.5 tabular-nums">
                                    {{ referenceFiles.length }} files
                                </span>
                            </div>

                            <!-- File navigation bar -->
                            <div class="flex items-center justify-between shrink-0 bg-white/[0.03] rounded-md px-2 py-1">
                                <button
                                    class="p-0.5 rounded transition-colors duration-200"
                                    :class="isFirstFile ? 'text-white/20 cursor-not-allowed' : 'text-white/60 hover:text-white hover:bg-white/10 cursor-pointer'"
                                    :disabled="isFirstFile"
                                    @click="selectPrevFile"
                                >
                                    <i class="mdi mdi-chevron-left text-base"></i>
                                </button>
                                <div class="flex items-center gap-2 min-w-0">
                                    <span class="text-[11px] text-white/40 tabular-nums shrink-0">{{ selectedOrderedIndex + 1 }}/{{ orderedFiles.length }}</span>
                                    <span class="text-[11px] text-white/60 truncate" :title="selectedFile">{{ formatFilename(selectedFile, 25) }}</span>
                                </div>
                                <button
                                    class="p-0.5 rounded transition-colors duration-200"
                                    :class="isLastFile ? 'text-white/20 cursor-not-allowed' : 'text-white/60 hover:text-white hover:bg-white/10 cursor-pointer'"
                                    :disabled="isLastFile"
                                    @click="selectNextFile"
                                >
                                    <i class="mdi mdi-chevron-right text-base"></i>
                                </button>
                            </div>

                            <!-- Image preview (main focus) -->
                            <div v-if="selectedFile && isImageFile(selectedFile)" class="flex flex-col gap-1.5 flex-1 min-h-0">
                                <div
                                    ref="containerRef"
                                    class="overflow-hidden w-full flex-1 min-h-[250px] max-h-[50dvh] rounded-md cursor-grab relative bg-echoes-background-gray"
                                    :class="{ 'cursor-grabbing': isDragging }"
                                    @mousedown="startDrag"
                                    @wheel="handleWheel"
                                >
                                    <img
                                        v-if="referenceFileUrl && !loadingReferenceFile"
                                        :src="referenceFileUrl"
                                        alt="Reference File"
                                        class="w-full h-full object-contain"
                                        :class="{ 'transition-transform duration-200': !isDragging }"
                                        :style="{ transform: `translate(${panX}px, ${panY}px) scale(${referenceFileZoom})` }"
                                    />
                                    <div v-else-if="loadingReferenceFile" class="w-full h-full flex items-center justify-center">
                                        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-echoes-primary"></div>
                                    </div>

                                    <div 
                                        v-if="showZoomIndicator"
                                        class="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1 pointer-events-none"
                                    >
                                        <i class="mdi mdi-magnify"></i>
                                        {{ referenceFileZoom.toFixed(1) }}x
                                    </div>
                                </div>
                                <div class="flex items-center justify-between gap-2 select-none shrink-0">
                                    <div :class="referenceFileZoom <= 1 ? 'button-zoom-disabled' : 'button-zoom'" @click="zoomToFit">
                                        <i class="mdi mdi-arrow-expand-all"></i>
                                    </div>
                                    <div class="flex items-center gap-1">
                                        <div :class="referenceFileZoom <= 1 ? 'button-zoom-disabled' : 'button-zoom'" @click="zoomOut">
                                            <i class="mdi mdi-minus"></i>
                                        </div>
                                        <input 
                                            type="range"
                                            min="1"
                                            max="5"
                                            step="0.1"
                                            v-model.number="referenceFileZoom"
                                            class="w-full h-1.5 bg-neutral-quaternary rounded-full appearance-none cursor-pointer"
                                            :style="{ backgroundImage: `linear-gradient(to right, #FE3E01 0%, #FE3E01 ${zoomPercentage}%, transparent ${zoomPercentage}%, transparent 100%)` }"
                                        >
                                        <div :class="referenceFileZoom >= 5 ? 'button-zoom-disabled' : 'button-zoom'" @click="zoomIn">
                                            <i class="mdi mdi-plus"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Non-image selected -->
                            <div v-else-if="selectedFile && !isImageFile(selectedFile)" class="flex-1 min-h-[100px] flex flex-col items-center justify-center gap-1 text-white/50">
                                <i class="mdi mdi-file-eye-off-outline text-3xl"></i>
                                <span class="text-xs">Preview not available for this file type</span>
                            </div>

                            <!-- File strips -->
                            <div class="border-t border-echoes-border-light pt-2 shrink-0 flex flex-col gap-2">
                                <!-- Images strip -->
                                <div v-if="imageFiles.length > 0">
                                    <div class="flex items-center gap-1.5 mb-1.5">
                                        <i class="mdi mdi-image-multiple-outline text-xs text-emerald-400/70"></i>
                                        <span class="text-[10px] text-white/40 uppercase tracking-wider">Images</span>
                                        <span class="text-[10px] text-white/30 tabular-nums">({{ imageFiles.length }})</span>
                                    </div>
                                    <div class="flex gap-1.5 overflow-x-auto custom-scrollbar-h pb-1">
                                        <div
                                            v-for="(f, idx) in imageFiles"
                                            :key="f.name"
                                            :ref="(el) => setThumbRef(idx, el)"
                                            class="ref-file-thumb"
                                            :class="{ 'ref-file-thumb-active': selectedOrderedIndex === idx }"
                                            @click="selectFile(idx)"
                                            :title="f.name"
                                        >
                                            <div class="size-11 rounded overflow-hidden bg-echoes-background-gray shrink-0">
                                                <img
                                                    v-if="thumbnailUrls[f.name]"
                                                    :src="thumbnailUrls[f.name]"
                                                    :alt="f.name"
                                                    class="w-full h-full object-cover"
                                                />
                                                <div v-else-if="loadingThumbnails.has(f.name)" class="w-full h-full flex items-center justify-center">
                                                    <div class="animate-spin rounded-full h-3 w-3 border-t border-b border-echoes-primary"></div>
                                                </div>
                                                <div v-else class="w-full h-full flex items-center justify-center text-white/20">
                                                    <i class="mdi mdi-image-outline text-sm"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Other files list -->
                                <div v-if="otherFiles.length > 0">
                                    <div class="flex items-center gap-1.5 mb-1.5">
                                        <i class="mdi mdi-file-outline text-xs text-blue-400/70"></i>
                                        <span class="text-[10px] text-white/40 uppercase tracking-wider">Other files</span>
                                        <span class="text-[10px] text-white/30 tabular-nums">({{ otherFiles.length }})</span>
                                    </div>
                                    <div class="flex gap-1 overflow-x-auto custom-scrollbar-h pb-1">
                                        <div
                                            v-for="(f, idx) in otherFiles"
                                            :key="f.name"
                                            :ref="(el) => setThumbRef(imageFiles.length + idx, el)"
                                            class="ref-file-other"
                                            :class="{ 'ref-file-other-active': selectedOrderedIndex === imageFiles.length + idx }"
                                            @click="selectFile(imageFiles.length + idx)"
                                            :title="f.name"
                                        >
                                            <i class="mdi text-xs shrink-0" :class="getFileIcon(f.name)"></i>
                                            <span class="truncate">{{ formatFilename(f.name, 18) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped lang="postcss">
    .button-zoom {
        @apply text-white/80 text-sm cursor-pointer hover:text-white transition-colors duration-200 ease-in-out bg-echoes-background-gray rounded-md p-1 px-1.5;
    }

    .button-zoom-disabled {
        @apply text-white/80 text-sm cursor-not-allowed transition-colors duration-200 ease-in-out bg-echoes-background-gray rounded-md p-1 px-1.5 opacity-50;
    }

    .button-copy {
        @apply px-2 py-0.5 border border-echoes-border-light rounded-md bg-echoes-background-gray cursor-pointer hover:bg-white/10 transition-colors duration-200 ease-in-out text-xs flex items-center gap-1;
    }

    .ref-file-thumb {
        @apply rounded-md cursor-pointer transition-all duration-200 
               border-2 border-transparent opacity-60 hover:opacity-100 shrink-0;
    }

    .ref-file-thumb-active {
        @apply opacity-100 border-echoes-primary;
    }

    .ref-file-other {
        @apply flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer text-[11px] text-white/50
               bg-white/[0.03] hover:bg-white/[0.08] transition-all duration-200 
               border border-transparent whitespace-nowrap shrink-0;
    }

    .ref-file-other-active {
        @apply text-white/90 bg-white/10 border-echoes-primary/50;
    }

    .task-content :deep(p) {
        font-size: inherit;
        line-height: inherit;
    }

    .task-content :deep(span.katex) {
        font-size: inherit !important;
    }
    
    .task-content :deep(.katex-display) {
        @apply my-2 !important;
    }

    .custom-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
    }

    .custom-scrollbar::-webkit-scrollbar {
        width: 5px;
    }

    .custom-scrollbar::-webkit-scrollbar-track {
        background: transparent;
    }

    .custom-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.15);
        border-radius: 9999px;
    }

    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.25);
    }

    .custom-scrollbar-h {
        scrollbar-width: thin;
        scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
    }

    .custom-scrollbar-h::-webkit-scrollbar {
        height: 4px;
    }

    .custom-scrollbar-h::-webkit-scrollbar-track {
        background: transparent;
    }

    .custom-scrollbar-h::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.15);
        border-radius: 9999px;
    }

    .custom-scrollbar-h::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.25);
    }

    .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        height: 14px;
        width: 14px;
        border-radius: 50%;
        background: #FE3E01;
        cursor: pointer;
        margin-top: -3px;
    }

    input[type="range"]::-moz-range-thumb {
        height: 14px;
        width: 14px;
        border: none;
        border-radius: 50%;
        background: #FE3E01;
        cursor: pointer;
    }

    input[type="range"]::-webkit-slider-runnable-track {
        width: 100%;
        height: 8px;
        cursor: pointer;
        background: transparent;
        border-radius: 9999px;
    }

    input[type="range"]::-moz-range-track {
        width: 100%;
        height: 8px;
        cursor: pointer;
        background: transparent;
        border-radius: 9999px;
    }
</style>
